import React from "react";
import { Image, Form } from "react-bootstrap";

/**
 * DataTablePagination
 *
 * Handles navigation between pages and controls for selecting the number
 * of rows displayed per page.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.query - Current table query state (page, pageSize, search, sort).
 * @param {Function} props.setQuery - Function to update query state.
 * @param {number} props.totalRecords - Total number of records available.
 * @param {number[]} props.pageSizeOptions - Options for page size dropdown.
 */
const DataTablePagination = ({
  query,
  setQuery,
  totalRecords,
  pageSizeOptions = [50, 100, 150, 200, 250],
}) => {
  // Ensure default pageSize is 50 if not set
  const pageSize = query.pageSize || 50;
  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setQuery((prev) => ({ ...prev, page, pageSize }));
    }
  };

  // Helper to get pagination buttons
  const getPaginationButtons = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      // Always show first 2, last 2, current, and ellipsis
      const firstPages = [1, 2, 3];
      const lastPages = [totalPages - 2, totalPages - 1, totalPages];
      if (query.page <= 4) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
      } else if (query.page >= totalPages - 3) {
        for (let i = 1; i <= 3; i++) pages.push(i);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        for (let i = 1; i <= 2; i++) pages.push(i);
        pages.push('...');
        for (let i = query.page - 1; i <= query.page + 1; i++) pages.push(i);
        pages.push('...');
        for (let i = totalPages - 1; i <= totalPages; i++) pages.push(i);
      }
    }
    return pages;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center p-3 table-footer">
        <Form.Select
          value={pageSize}
          onChange={(e) =>
            setQuery((prev) => ({
              ...prev,
              pageSize: Number(e.target.value),
              page: 1,
            }))
          }
          style={{
            width: 65,
            fontSize: "12px",
            fontWeight: 400,
            color: "#1F3335",
            border: "1px solid #CFD1D0",
            borderRadius: "8px",
            backgroundColor: "white",
            cursor: "pointer",
            padding: "4px 8px",
          }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Form.Select>
        <div className="pagination">
          <button
            disabled={query.page === 1}
            onClick={() => changePage(1)}
            className="previous-btn-dataTable me-2 d-flex align-items-center gap-2"
          >
            <img src="/assets/images/double-arrow-previous.svg" />
            First
          </button>
          <button
            disabled={query.page === 1}
            onClick={() => changePage(query.page - 1)}
            className="previous-btn-dataTable me-2 d-flex align-items-center"
          >
            <Image
              src="/assets/images/arrow-previous.svg"
              width={14}
              className="me-2"
            />
            Previous
          </button>

          {getPaginationButtons().map((p, idx) =>
            p === '...'
              ? <span key={"ellipsis-" + idx} className="mx-1">...</span>
              : (
                <button
                  key={p}
                  className={
                    p === query.page
                      ? "previous-btn-dataTable-active mx-1"
                      : "previous-btn-dataTable mx-1"
                  }
                  onClick={() => changePage(p)}
                >
                  {p}
                </button>
              )
          )}

          <button
            disabled={query.page === totalPages}
            onClick={() => changePage(query.page + 1)}
            className="previous-btn-dataTable ms-2"
          >
            Next
            <Image
              src="/assets/images/arrow-next.svg"
              width={14}
              className="ms-2"
            />
          </button>
          <button
            disabled={query.page === totalPages}
            onClick={() => changePage(totalPages)}
            className="previous-btn-dataTable ms-2 d-flex align-items-center gap-2"
          >
            Last
            <img src="/assets/images/double-arrow-next.svg" />
          </button>
        </div>
      </div>
    </>
  );
};

export default DataTablePagination;
